import EmojiSelector from 'controls/dropdowns/emoji_selector'
import { useMediaQuery } from 'hooks/use_media_query'
import { EmojiIcon } from 'images'
import PropTypes from 'prop-types'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { deleteChat } from 'reduxes/slices/chats/actions'
import {
  getCurrentTeammateHandle,
  isConfirmedSelector,
} from 'reduxes/slices/current/selectors'
import { deleteNote } from 'reduxes/slices/notes/actions'
import { getSelectedTopicId } from 'reduxes/slices/selected/selectors'
import { toggleEmojiReaction } from 'reduxes/slices/timelines/actions'
import { createThreadTopic } from 'reduxes/slices/topics/actions'

import { MEDIA_QUERIES } from 'design'
import { Button, buttonStyles } from 'primitives/button'
import { Stack } from 'primitives/containers'
import * as Popover from 'primitives/popover'
import { cn } from 'util/cn'

import { useNavToTopic } from '../../../hooks/app_location/use_nav_to_topic'
import { setSelectedTimelineId } from '../../../reduxes/slices/selected/actions'
import { setReplyTimelineId } from '../../../reduxes/slices/timelines/actions'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import { TimelinesFocusContext } from '../timelines_focus_manager'
import {useAxios} from "../../../hooks/use_axios";

const EMOJI_SELECTOR_OFFSET = 12

export default function ActionsBar({ timeline, item }) {
  const popoverTriggerRef = useRef()
  const dispatch = useDispatch()
  const axios = useAxios()
  const currentTeammateHandle = useSelector(getCurrentTeammateHandle)
  const selectedTopicId = useSelector(getSelectedTopicId)
  const timelineId = timeline?.id
  const confirmed = useSelectorDelegateStore(isConfirmedSelector)
  const navToTopic = useNavToTopic()
  const { setFocusLock } = useContext(TimelinesFocusContext)

  const desktop = useMediaQuery(MEDIA_QUERIES.md)

  const deleteThisTimelineItem = useCallback(() => {
    let promise
    switch (timeline.tl_type) {
      case 'Note':
        promise = dispatch(axios(deleteNote(item)))
        break
      case 'Chat':
        promise = dispatch(axios(deleteChat(item)))
        break
    }
    promise.then(() => {
      dispatch(setSelectedTimelineId(null))
    })
  }, [axios, dispatch, item, timeline.tl_type])

  const selectThisTimeline = useCallback(() => {
    if (!timeline) {
      return
    }

    dispatch(setSelectedTimelineId(timeline.id))
  }, [dispatch, timeline])

  const createThread = useCallback(() => {
    if (!timeline) {
      return
    }

    dispatch(axios(createThreadTopic(timeline.id, timeline.topic_id))).then(
      (topicId) => {
        navToTopic(topicId)
      },
    )
  }, [axios, dispatch, navToTopic, timeline])

  const thumbsup = useCallback(() => {
    if (!timeline) {
      return
    }

    dispatch(axios(toggleEmojiReaction(timeline.topic_id, timeline.id, ':+1:')))
  }, [axios, dispatch, timeline])

  const replyToTimeline = useCallback(() => {
    if (!timeline) {
      return
    }

    dispatch(setReplyTimelineId(timeline.topic_id, timeline.id))
  }, [dispatch, timeline])

  const popoverContentRef = useRef(null)
  const [open, setOpen] = useState(false)

  const onOpenChange = useCallback(
    (open) => {
      if (desktop && setFocusLock) {
        setFocusLock(open ? timelineId : undefined)
      }
      setOpen(open)
    },
    [desktop, setFocusLock, timelineId],
  )

  useEffect(() => {
    const popoverContent = popoverContentRef.current
    const popoverTrigger = popoverTriggerRef.current
    if (!open || !popoverContent || !popoverTrigger) {
      return
    }

    if (desktop) {
      popoverContent.style.transform = ''
      return
    }

    const { right } = popoverTrigger.getBoundingClientRect()
    const emojiSelectorOffset =
      window.innerWidth - right - EMOJI_SELECTOR_OFFSET

    popoverContent.style.transform = `translateX(${emojiSelectorOffset}px)`
  }, [desktop, open])

  const canReply =
    timeline.tl_type === 'Chat' && timeline.topic_id === selectedTopicId
  const canThread = timeline.can_thread && confirmed

  const key = `${timeline.ref_name}_${timeline.tl_id}`

  return (
    <Stack
      direction="row"
      gap="2"
      className={cn('timeline-actions-bar')}
      role="group"
      css={{
        position: 'absolute',
        right: 8,
        top: 8,
      }}
    >
      <Button
        id={'thread-btn-thumb-' + key}
        className="timeline-action-btn"
        onClick={thumbsup}
        tooltip="Thumbs up"
        shape="square"
        size="small"
        variant="tertiary"
        title="Thumbs up"
        data-testid="timeline-actions-bar__thumbsup"
      >
        <i className={'fas fa-thumbs-up'} />
      </Button>

      <Popover.Root onOpenChange={onOpenChange}>
        <Popover.Trigger
          ref={popoverTriggerRef}
          id={`react-btn-tl-${key}`}
          className={cn(
            buttonStyles({
              shape: 'square',
              size: 'small',
              variants: 'tertiary',
            }),
            'timeline-action-btn',
          )}
          title="Add reaction"
          data-testid="timeline-actions-bar__add-reaction"
        >
          <div>
            <EmojiIcon />
          </div>
        </Popover.Trigger>
        <Popover.Content ref={popoverContentRef} side="top">
          <EmojiSelector timelineId={timelineId} />
        </Popover.Content>
      </Popover.Root>

      {canThread && (
        <Button
          id={'thread-btn-tl-' + key}
          className="timeline-action-btn"
          onClick={createThread}
          tooltip="Reply in new Topic"
          shape="square"
          size="small"
          variant="tertiary"
          title="Reply in new Topic"
          data-testid="timeline-actions-bar__reply-in-new-topic"
        >
          <i className={'fas fa-code-branch'} />
        </Button>
      )}
      {canReply && (
        <Button
          id={'reply-btn-tl-' + key}
          className="timeline-action-btn"
          onClick={replyToTimeline}
          tooltip="Reply"
          shape="square"
          size="small"
          variant="tertiary"
          title="Reply"
          data-testid="timeline-actions-bar__reply"
        >
          <i className="fas fa-reply" />
        </Button>
      )}

      {item && item.handle === currentTeammateHandle && (
        <>
          <Button
            id={'edit-btn-tl-' + key}
            className="timeline-action-btn"
            onClick={selectThisTimeline}
            tooltip="Edit"
            shape="square"
            size="small"
            variant="tertiary"
            title="Edit"
            data-testid="timeline-actions-bar__edit"
          >
            <i className="fas fa-pencil-alt" />
          </Button>
          <Button
            id={'delete-btn-tl-' + key}
            className="timeline-action-btn"
            onClick={deleteThisTimelineItem}
            tooltip="Delete"
            shape="square"
            size="small"
            variant="tertiary"
            title="Delete"
            data-testid="timeline-actions-bar__delete"
          >
            <i className="fas fa-trash-alt" />
          </Button>
        </>
      )}
    </Stack>
  )
}

ActionsBar.propTypes = {
  timeline: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
}
